import { useMemo, useRef } from "react";
import { useSubscription, useQuery } from "./urqlHooks";
import { useSelector } from "react-redux";
import { botIndex } from "../utils/botList";
import moment from "moment";
import { hasWsAuthToken } from "./wsClient";
import { getUrqlTenantCompanyId } from "./tenantCompanyId";
import {
  getFriendlyBotlogTitle,
  getFriendlyBotlogToastContent,
  isReconciliationBotlog,
} from "../utils/reconciliationFriendlyMessages";

const BOT_LOGS = `subscription BOT_LOGS($companyId: String!, $userId: String) {
    botlogResponse(companyId: $companyId, userId: $userId) {
      id
      empresa
      botname
      status
      mssg
      userId
    }
  }`;

const formatBotlogNotification = (botlogResponse) => {
  if (!botlogResponse) return null;

  const botnamesWithStatus = [
    "F29/Status",
    "f29-email",
    "cobranza-email",
    "PREVIRED/imposiciones",
    "sii",
    "sii-reconstruccion",
    "bsaleApiToken",
  ];
  const useStatusForResult = botnamesWithStatus.includes(botlogResponse?.botname);
  const botname = botlogResponse?.botname;
  const isReconciliation = isReconciliationBotlog(botname);
  const ok =
    botlogResponse.mssg === "Ejecutado de forma correcta"
      ? true
      : useStatusForResult || isReconciliation
      ? Boolean(botlogResponse?.status)
      : false;
  const displayMssg = isReconciliation
    ? getFriendlyBotlogToastContent(botname, botlogResponse.mssg, ok)
    : botlogResponse.mssg;

  return {
    id: botlogResponse.id || null,
    userId: botlogResponse.userId || null,
    title: isReconciliation
      ? getFriendlyBotlogTitle(botname)
      : botIndex[botname] || botname,
    description:
      botlogResponse.mssg === "Ejecutado de forma correcta"
        ? "Ejecutado de forma correcta"
        : useStatusForResult
        ? displayMssg
        : isReconciliation
        ? displayMssg
        : "Ejecucion con errores",
    ejecucionCorrecta:
      botlogResponse.mssg === "Ejecutado de forma correcta"
        ? true
        : useStatusForResult
        ? botlogResponse?.status
        : false,
    ago: moment().format("DD-MM-YYYY HH:mm:ss"),
    visited: false,
    botName: botname,
    mssg: displayMssg,
    rawMssg: botlogResponse.mssg,
    ok,
  };
};

/** Dedupe: varias suscripciones WS / remounts / re-publishes no deben spamear toasts. */
const seenBotlogToastIds = new Map();
const BOTLOG_TOAST_DEDUPE_MS = 15000;

export const consumeBotlogToastOnce = (botData) => {
  if (!botData) return false;
  const contentKey = `${botData.botName || ""}|${botData.rawMssg || botData.mssg || ""}`;
  const keys = [botData.id, contentKey].filter(Boolean);
  const now = Date.now();
  for (const key of keys) {
    const prev = seenBotlogToastIds.get(key);
    if (prev && now - prev < BOTLOG_TOAST_DEDUPE_MS) {
      return false;
    }
  }
  for (const key of keys) {
    seenBotlogToastIds.set(key, now);
  }
  if (seenBotlogToastIds.size > 200) {
    for (const [k, t] of seenBotlogToastIds) {
      if (now - t > BOTLOG_TOAST_DEDUPE_MS) seenBotlogToastIds.delete(k);
    }
  }
  return true;
};

export const SubcribeWS = (companyId) => {
  const uid = useSelector((state) => state.auth?.uid);
  const companyIdStr = String(companyId || "");
  const userIdStr = uid ? String(uid) : null;
  const lastPayloadRef = useRef(null);

  const [res] = useSubscription({
    query: BOT_LOGS,
    variables: {
      companyId: companyIdStr,
      userId: userIdStr,
    },
    // No abrir WS sin JWT: botlogs (@SkipAuth) abría la conexión sin connectionParams y la campana fallaba.
    pause: !companyIdStr || !hasWsAuthToken(),
  });

  const formatted = useMemo(() => {
    const raw = res.data?.botlogResponse;
    if (!raw) return undefined;
    // urql puede re-emitir el mismo payload en re-render; no inventar un toast nuevo.
    if (lastPayloadRef.current === raw) {
      return undefined;
    }
    lastPayloadRef.current = raw;
    const formattedItem = formatBotlogNotification(raw);
    return formattedItem ? [formattedItem] : undefined;
  }, [res.data]);

  return {
    ...res,
    data: formatted,
  };
};

const LAST_BOT_DATE = `query UpdateDocsDate($botname: String!, $empresa: String!) {
  UpdateDocsDate(botname: $botname, empresa: $empresa) {
    id
    status
    empresa
    botname
    botend
    mssg
  }
}`;

export const BotUpdateDate = (botname, empresa) => {
  const [result, reexecuteQuery] = useQuery({
    query: LAST_BOT_DATE,
    variables: { botname, empresa },
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
}

const FIND_LAST_BOTLOG = `query FindLastBotlog($passwordId: String!, $companyId: String!){
  FindLastBotlog(passwordId: $passwordId, companyId: $companyId){
    id
    botend
    status
    mssg
  }
}`;

export const FindLastBotlog = (passwordId) => {
  const companyId = getUrqlTenantCompanyId();
  const [result, reexecuteQuery] = useQuery({
    query: FIND_LAST_BOTLOG,
    variables: {passwordId, companyId},
    pause: !passwordId || !companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
}
